import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Card } from '../components/Card';
import { Pill } from '../components/Pill';
import { Modal } from '../components/Modal';
import { DataTable, type Column } from '../components/DataTable';
import { CopyField } from '../components/CopyField';
import { Icon } from '../components/Icons';
import { btnGhost, btnPrimary } from '../components/buttons';
import { adminApi } from '../api/admin';
import { fmtDate, fmtNum, fmtUSD } from '../lib/format';
import type { CreatorWithStatsDto } from '../api/types';
import type { ApiError } from '../api/client';

const Schema = z.object({
  email: z.string().email('Valid email required'),
  handle: z.string().min(3).max(50).regex(/^[a-zA-Z0-9_.-]+$/, 'Letters, digits, underscore, dot, hyphen'),
  name: z.string().min(2).max(100),
  country: z.string().max(2).optional(),
  primaryCode: z
    .string()
    .min(3)
    .max(32)
    .regex(/^[A-Za-z0-9_-]+$/, 'Letters, digits, underscore, hyphen')
    .optional()
    .or(z.literal('')),
});
type FormValues = z.infer<typeof Schema>;

export function CreatorsListScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended'>('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<{ creator: CreatorWithStatsDto; tempPassword: string } | null>(null);

  const creators = useQuery({
    queryKey: ['admin', 'creators', statusFilter],
    queryFn: () => adminApi.listCreators(statusFilter === 'all' ? undefined : statusFilter),
  });

  const filtered = (creators.data ?? []).filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.handle.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q)
    );
  });

  const columns: Column<CreatorWithStatsDto>[] = [
    {
      key: 'name',
      header: 'Creator',
      sortKey: 'name',
      render: (c) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'var(--brand)',
              color: 'var(--bg-elev)',
              display: 'grid',
              placeItems: 'center',
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            {c.avatarInitials}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
              @{c.handle}
            </div>
          </div>
        </div>
      ),
    },
    { key: 'email', header: 'Email', sortKey: 'email', hideMobile: true, render: (c) => <span className="mono" style={{ fontSize: 12 }}>{c.email}</span> },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (c) =>
        c.status === 'active' ? (
          <Pill tone="good" size="sm">Active</Pill>
        ) : (
          <Pill tone="bad" size="sm">Suspended</Pill>
        ),
    },
    {
      key: 'activeSubs',
      header: 'Active subs',
      align: 'right',
      sortKey: (c) => c.stats.activeSubs,
      render: (c) => <span className="num">{fmtNum(c.stats.activeSubs)}</span>,
    },
    {
      key: 'lifetime',
      header: 'Lifetime earnings',
      align: 'right',
      sortKey: (c) => c.stats.lifetimeCommissionCents,
      render: (c) => <span className="num" style={{ fontWeight: 600 }}>{fmtUSD(c.stats.lifetimeCommissionCents)}</span>,
    },
    {
      key: 'joined',
      header: 'Joined',
      hideMobile: true,
      sortKey: (c) => c.joinedAt,
      render: (c) => <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>{fmtDate(c.joinedAt)}</span>,
    },
  ];

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 18, maxWidth: 1280 }}>
      <Card padding={0}>
        <DataTable<CreatorWithStatsDto>
          rows={filtered}
          columns={columns}
          rowKey={(c) => c.id}
          onRowClick={(c) => navigate(`/creators/${c.id}`)}
          search={{ value: search, onChange: setSearch, placeholder: 'Search name, handle, email…' }}
          toolbar={
            <>
              <div style={{ display: 'flex', gap: 2, background: 'var(--bg-sunken)', padding: 3, borderRadius: 'var(--r-pill)' }}>
                {(['all', 'active', 'suspended'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 'var(--r-pill)',
                      fontSize: 12,
                      fontWeight: 500,
                      background: statusFilter === s ? 'var(--bg-elev)' : 'transparent',
                      color: statusFilter === s ? 'var(--ink)' : 'var(--ink-3)',
                      boxShadow: statusFilter === s ? 'var(--shadow-card)' : 'none',
                      textTransform: 'capitalize',
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <button
                style={btnPrimary}
                onClick={() => {
                  setCreated(null);
                  setCreateOpen(true);
                }}
              >
                <Icon.Plus />
                Create creator
              </button>
            </>
          }
          emptyState={creators.isLoading ? 'Loading…' : 'No creators match your filters.'}
        />
      </Card>

      <CreateCreatorModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreated(null);
        }}
        onCreated={(c) => {
          setCreated(c as any);
          qc.invalidateQueries({ queryKey: ['admin', 'creators'] });
          qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
        }}
        created={created}
      />
    </div>
  );
}

function CreateCreatorModal({
  open,
  onClose,
  onCreated,
  created,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (r: { creator: any; tempPassword: string }) => void;
  created: { creator: any; tempPassword: string } | null;
}) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: { email: '', handle: '', name: '', country: '', primaryCode: '' },
  });
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      adminApi.createCreator({
        email: v.email.trim(),
        handle: v.handle.trim(),
        name: v.name.trim(),
        country: v.country?.trim() || undefined,
        primaryCode: v.primaryCode?.trim() ? v.primaryCode.trim().toUpperCase() : undefined,
      }),
    onSuccess: (res) => {
      onCreated(res);
      reset();
    },
    onError: (err: ApiError) => setFormError(err?.message ?? 'Create failed'),
  });

  const onSubmit = (v: FormValues) => {
    const parsed = Schema.safeParse(v);
    if (!parsed.success) {
      setFormError(parsed.error.errors[0]?.message ?? 'Invalid input');
      return;
    }
    setFormError(null);
    mutation.mutate(parsed.data);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={created ? 'Creator created' : 'Create creator'}
      description={
        created
          ? 'Copy the temp password and share it with the creator out-of-band — it will not be shown again.'
          : 'Generate a one-time temporary password the creator uses for their first login.'
      }
      footer={
        created ? (
          <button style={btnPrimary} onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button style={btnGhost} onClick={onClose} type="button">
              Cancel
            </button>
            <button
              style={btnPrimary}
              type="submit"
              form="create-creator-form"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Creating…' : 'Create'}
            </button>
          </>
        )
      }
      width={520}
    >
      {created ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
              Created
            </div>
            <div style={{ marginTop: 4, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600 }}>
              {created.creator.name} <span style={{ color: 'var(--ink-3)' }}>· @{created.creator.handle}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
              {created.creator.email}
            </div>
          </div>
          <CopyField value={created.tempPassword} label="Temp pw" big />
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            Share via Signal/DM/encrypted email. The creator must change this on first login.
          </div>
        </div>
      ) : (
        <form id="create-creator-form" onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <ModalField label="Name" error={errors.name?.message}>
            <input {...register('name')} autoFocus placeholder="Marin Okafor" style={inputStyle} />
          </ModalField>
          <ModalField label="Email" error={errors.email?.message}>
            <input {...register('email')} placeholder="creator@example.com" style={inputStyle} />
          </ModalField>
          <ModalField label="Handle" error={errors.handle?.message}>
            <input {...register('handle')} placeholder="pixelroute" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </ModalField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
            <ModalField label="Country" error={errors.country?.message}>
              <input
                {...register('country')}
                placeholder="US"
                maxLength={2}
                style={{ ...inputStyle, fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}
              />
            </ModalField>
            <ModalField label="Primary code (optional)" error={errors.primaryCode?.message}>
              <input
                {...register('primaryCode')}
                placeholder="PIXEL10"
                style={{ ...inputStyle, fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}
              />
            </ModalField>
          </div>
          {formError && (
            <div
              style={{
                fontSize: 12.5,
                color: 'var(--bad)',
                background: 'color-mix(in oklab, var(--bad) 10%, var(--bg-elev))',
                border: '1px solid color-mix(in oklab, var(--bad) 25%, transparent)',
                padding: '8px 12px',
                borderRadius: 'var(--r-sm)',
              }}
            >
              {formError}
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}

const inputStyle = {
  background: 'var(--bg-elev)',
  border: '1px solid var(--line-strong)',
  borderRadius: 'var(--r-md)',
  padding: '10px 12px',
  fontSize: 14,
  color: 'var(--ink)',
  outline: 'none',
  width: '100%',
} as const;

function ModalField({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 11.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
        {label}
      </span>
      {children}
      {error && <span style={{ fontSize: 11, color: 'var(--bad)' }}>{error}</span>}
    </label>
  );
}
